'use client'

import * as React from 'react'
import { Input } from './input'
import { Label } from './label'
import { cn } from '@/lib/utils'

/**
 * DateRangeFields — a labeled start/end pair of native date inputs.
 * Native <input type="date"> keeps keyboard and mobile pickers for free;
 * the native calendar indicator is the affordance (no overlaid icon).
 * When end < start both fields render invalid (Input's danger idiom);
 * callers use isDateRangeInvalid with the same values to gate their
 * submit button and surface the guard message in their own live region.
 */

/** True when both dates are set and the end lands before the start. */
export function isDateRangeInvalid(start: string, end: string): boolean {
    return Boolean(start && end && end < start)
}

type DateRangeFieldsProps = {
    idPrefix: string
    startValue: string
    endValue: string
    onStartChange: (value: string) => void
    onEndChange: (value: string) => void
    startLabel?: string
    endLabel?: string
    className?: string
}

export function DateRangeFields({
    idPrefix,
    startValue,
    endValue,
    onStartChange,
    onEndChange,
    startLabel = 'Start date',
    endLabel = 'End date',
    className,
}: DateRangeFieldsProps) {
    const invalid = isDateRangeInvalid(startValue, endValue)

    return (
        <div className={cn('grid grid-cols-2 gap-4', className)}>
            <div>
                <Label htmlFor={`${idPrefix}-start`} className="mb-1 block">
                    {startLabel}
                </Label>
                <Input
                    id={`${idPrefix}-start`}
                    type="date"
                    value={startValue}
                    onChange={(e) => onStartChange(e.target.value)}
                    error={invalid}
                />
            </div>
            <div>
                <Label htmlFor={`${idPrefix}-end`} className="mb-1 block">
                    {endLabel}
                </Label>
                <Input
                    id={`${idPrefix}-end`}
                    type="date"
                    value={endValue}
                    min={startValue || undefined}
                    onChange={(e) => onEndChange(e.target.value)}
                    error={invalid}
                />
            </div>
        </div>
    )
}
